/** Canonical newsletter status slugs (must match DB newsletters_status_check). */

export const NEWSLETTER_STATUSES = [
  'draft',
  'on_hold',
  'scheduled',
  'ready_to_publish',
  'published',
] as const;

export type NewsletterStatus = (typeof NEWSLETTER_STATUSES)[number];

/** Human-readable labels for UI display only. */
export const NEWSLETTER_STATUS_LABELS: Record<NewsletterStatus, string> = {
  draft: 'Draft',
  on_hold: 'On Hold',
  scheduled: 'Scheduled',
  ready_to_publish: 'Ready to Publish',
  published: 'Published',
};

/** Settings status dropdown: slug value, human label. */
export const NEWSLETTER_SETTINGS_STATUS_OPTIONS: {
  value: NewsletterStatus;
  label: string;
}[] = [
  { value: 'draft', label: NEWSLETTER_STATUS_LABELS.draft },
  { value: 'on_hold', label: NEWSLETTER_STATUS_LABELS.on_hold },
  { value: 'scheduled', label: NEWSLETTER_STATUS_LABELS.scheduled },
];

const LEGACY_LABEL_TO_SLUG = new Map<string, NewsletterStatus>([
  ['draft', 'draft'],
  ['on hold', 'on_hold'],
  ['scheduled', 'scheduled'],
  ['ready to publish', 'ready_to_publish'],
  ['published', 'published'],
]);

const SLUG_SET = new Set<string>(NEWSLETTER_STATUSES);

function statusLookupKeys(statusRaw: string): string[] {
  const trimmed = statusRaw.trim();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();
  const slugish = lower.replace(/[\s-]+/g, '_');
  return slugish === lower ? [slugish, lower] : [slugish, lower];
}

export function newsletterStatusKey(statusRaw: string | null | undefined): string {
  return normalizeNewsletterStatus(statusRaw);
}

/** Coerce slug, legacy Title Case label, or mixed input to canonical DB slug. */
export function normalizeNewsletterStatus(
  statusRaw: string | null | undefined,
  fallback: NewsletterStatus = 'draft',
): NewsletterStatus {
  if (statusRaw == null || !String(statusRaw).trim()) return fallback;
  for (const key of statusLookupKeys(String(statusRaw))) {
    if (SLUG_SET.has(key)) return key as NewsletterStatus;
    const legacy = LEGACY_LABEL_TO_SLUG.get(key.replace(/_/g, ' '));
    if (legacy) return legacy;
    const legacyUnderscore = LEGACY_LABEL_TO_SLUG.get(key);
    if (legacyUnderscore) return legacyUnderscore;
  }
  return fallback;
}

/** Display label for any stored or legacy status string. */
export function newsletterStatusLabel(statusRaw: string | null | undefined): string {
  return NEWSLETTER_STATUS_LABELS[normalizeNewsletterStatus(statusRaw)];
}

/** @deprecated Use normalizeNewsletterStatus — slug is already the stored value. */
export function newsletterStatusToSlug(
  statusRaw: string | null | undefined,
  fallback: NewsletterStatus = 'draft',
): NewsletterStatus {
  return normalizeNewsletterStatus(statusRaw, fallback);
}

export function isNewsletterStatusCommitted(status: string | null | undefined): boolean {
  const s = normalizeNewsletterStatus(status);
  return s === 'scheduled' || s === 'ready_to_publish' || s === 'published';
}

/**
 * Statuses where Queue recipients / Send now are available.
 * Must match the materialize-newsletter edge fn and materialize_newsletter_sends RPC.
 */
export function canQueueNewsletterRecipients(status: string | null | undefined): boolean {
  const s = normalizeNewsletterStatus(status);
  return s === 'draft' || s === 'scheduled';
}

/** Settings status dropdown is locked after materialize. */
export function isNewsletterStatusLocked(status: string | null | undefined): boolean {
  const s = normalizeNewsletterStatus(status);
  return s === 'ready_to_publish' || s === 'published';
}
